import React, { useEffect, useState, useContext } from "react";
import { useStateCotext } from "../../Context/ContextProvider";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import { MdOutlineClose } from "react-icons/md";
import { Bell, Calendar, Menu as MenuIcon, CheckCircle, XCircle, Clock, MessageSquare } from "lucide-react";
import moment from "moment";
import MainHeaderDropdownMenu from "../MainHeaderDropdownMenu";
import logo2 from "../media/gold-logo2.png";
import { AuthContext } from "../../Context/AuthContextProvider";

const Navbar = () => {
  const { activeMenu, setActiveMenu, setScreenSize, screenSize } =
    useStateCotext();

  const [currentDateTime, setCurrentDateTime] = useState(
    moment().format("MMMM Do YYYY, h:mm:ss a")
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDateTime(moment().format("MMMM Do YYYY, h:mm:ss A"));
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize]);

  const handleCloseSideBar = () => {
    if (activeMenu !== undefined && screenSize <= 900) {
      setActiveMenu(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-[#101c4c] to-[#1a2d5c] shadow-lg">
      <div className="flex h-20 justify-between items-center px-4 md:px-6">
        {/* Left Section */}
        {window.innerWidth < 900 ? (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveMenu(!activeMenu)}
              className="p-2 rounded-lg hover:bg-white/10 transition-all duration-200 text-white"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
            <Link
              to="/"
              onClick={handleCloseSideBar}
              className="flex items-center"
            >
              <img
                src={logo2}
                alt="Brand Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-white/90">
            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
              <Calendar className="w-5 h-5 text-[#fcdb6a]" />
            </div>
            <div className="flex flex-col">
              <p className="text-xs text-white/60 font-medium">
                {moment().format("dddd")}
              </p>
              <p className="text-sm font-semibold">{currentDateTime}</p>
            </div>
          </div>
        )}

        {/* Right Section */}
        <div className="flex items-center gap-3">
          <Notifications />
          <MainHeaderDropdownMenu />
        </div>
      </div>
    </div>
  );
};

export default Navbar;

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
    marginTop: theme.spacing(1),
    minWidth: 320,
    maxWidth: 400,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "0 10px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(185, 150, 83, 0.1)",
    border: "1px solid rgba(185, 150, 83, 0.2)",
    "& .MuiMenu-list": {
      padding: "12px",
      background: "linear-gradient(180deg, #ffffff 0%, #fef9f0 100%)",
    },
  },
}));

export function Notifications() {
  const { userSubmissions } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifications, setNotifications] = React.useState([]);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Generate notifications from user submissions
  useEffect(() => {
    if (!userSubmissions || userSubmissions.length === 0) {
      setNotifications([]);
      return;
    }

    const notifs = [];

    // Check each submission for notifications
    userSubmissions.forEach((submission) => {
      const daysSinceUpdate = moment().diff(moment(submission.updatedAt || submission.createdAt), 'days');

      // Only show recent notifications (within 7 days)
      if (daysSinceUpdate <= 7) {
        // Approved domains
        if (submission.status === "Active") {
          notifs.push({
            id: `approved-${submission._id}`,
            type: "success",
            icon: CheckCircle,
            title: "Domain Approved!",
            message: `${submission.name} is now live on marketplace`,
            time: moment(submission.updatedAt || submission.createdAt).fromNow(),
            link: `/Marketplace_Product/${submission._id}`,
          });
        }

        // Rejected domains
        if (submission.status === "removed") {
          notifs.push({
            id: `rejected-${submission._id}`,
            type: "error",
            icon: XCircle,
            title: "Submission Rejected",
            message: `${submission.name} didn't meet requirements`,
            time: moment(submission.updatedAt || submission.createdAt).fromNow(),
            link: "/Dashboard/MySubmissions",
          });
        }

        // Pending review (older than 2 days)
        if (submission.status === "pending" && daysSinceUpdate >= 2) {
          notifs.push({
            id: `pending-${submission._id}`,
            type: "warning",
            icon: Clock,
            title: "Under Review",
            message: `${submission.name} is awaiting admin approval`,
            time: moment(submission.createdAt).fromNow(),
            link: "/Dashboard/MySubmissions",
          });
        }
      }

      // New inquiries (regardless of age)
      if (submission.inquiries && submission.inquiries.length > 0) {
        const recentInquiries = submission.inquiries.filter(inq => {
          const inquiryAge = moment().diff(moment(inq.createdAt), 'days');
          return inquiryAge <= 3; // Show inquiries from last 3 days
        });

        if (recentInquiries.length > 0) {
          notifs.push({
            id: `inquiry-${submission._id}`,
            type: "info",
            icon: MessageSquare,
            title: "New Inquiry",
            message: `${recentInquiries.length} inquiry on ${submission.name}`,
            time: moment(recentInquiries[0].createdAt).fromNow(),
            link: `/Dashboard/Inquiries/${submission._id}`,
          });
        }
      }
    });

    // Sort by most recent first
    notifs.sort((a, b) => {
      const timeA = a.time.includes('ago') ? moment().subtract(1, 'hour') : moment();
      const timeB = b.time.includes('ago') ? moment().subtract(1, 'hour') : moment();
      return timeB - timeA;
    });

    // Limit to 5 most recent notifications
    setNotifications(notifs.slice(0, 5));
  }, [userSubmissions]);

  return (
    <div>
      <Button
        id="notification-button"
        aria-controls={open ? "notification-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          minWidth: "unset",
          width: "44px",
          height: "44px",
          padding: 0,
          borderRadius: "12px",
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          color: "white",
          transition: "all 0.3s ease",
          position: "relative",
          "&:hover": {
            background: "rgba(255, 255, 255, 0.15)",
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(185, 150, 83, 0.3)",
          },
        }}
      >
        <Bell className="w-5 h-5" />
        {notifications.length > 0 && (
          <span
            style={{
              position: "absolute",
              top: "6px",
              right: "6px",
              width: "8px",
              height: "8px",
              background: "#ef4444",
              borderRadius: "50%",
              border: "2px solid #101c4c",
            }}
          />
        )}
      </Button>

      <StyledMenu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
            padding: "8px 4px",
          }}
        >
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-lg">
              <Bell className="w-4 h-4 text-white" />
            </div>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #b99653 0%, #d4af37 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: "18px",
              }}
            >
              Notifications
            </Typography>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <MdOutlineClose className="text-gray-500 text-xl" />
          </button>
        </Box>

        <Divider
          sx={{ margin: "8px 0", borderColor: "rgba(185, 150, 83, 0.2)" }}
        />

        {/* Content */}
        <Box sx={{ padding: "12px 8px", maxHeight: "400px", overflowY: "auto" }}>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              {/* Empty State Icon */}
              <div className="w-20 h-20 mb-4 bg-gradient-to-br from-[#fcdb6a]/20 to-[#b99653]/10 rounded-full flex items-center justify-center">
                <Bell className="w-10 h-10 text-[#b99653]" />
              </div>

              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: "#6b7280",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                No notifications yet
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: "14px",
                  maxWidth: "250px",
                }}
              >
                We'll notify you when something important happens
              </Typography>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notif) => (
                <Link
                  key={notif.id}
                  to={notif.link}
                  onClick={handleClose}
                  className="block"
                >
                  <div className={`p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                    notif.type === "success" ? "bg-green-50 border-green-200 hover:bg-green-100" :
                    notif.type === "error" ? "bg-red-50 border-red-200 hover:bg-red-100" :
                    notif.type === "warning" ? "bg-yellow-50 border-yellow-200 hover:bg-yellow-100" :
                    "bg-blue-50 border-blue-200 hover:bg-blue-100"
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        notif.type === "success" ? "bg-green-100" :
                        notif.type === "error" ? "bg-red-100" :
                        notif.type === "warning" ? "bg-yellow-100" :
                        "bg-blue-100"
                      }`}>
                        <notif.icon className={`w-4 h-4 ${
                          notif.type === "success" ? "text-green-600" :
                          notif.type === "error" ? "text-red-600" :
                          notif.type === "warning" ? "text-yellow-600" :
                          "text-blue-600"
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">
                          {notif.title}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {notif.time}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Box>

        <Divider
          sx={{ margin: "8px 0", borderColor: "rgba(185, 150, 83, 0.2)" }}
        />

        {/* Footer Button */}
        {notifications.length > 0 && (
          <Link to="/Dashboard/MySubmissions" onClick={handleClose}>
            <Button
              fullWidth
              sx={{
                marginTop: "8px",
                padding: "10px",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                background: "linear-gradient(135deg, #b99653 0%, #d4af37 100%)",
                color: "white",
                boxShadow: "0 4px 12px rgba(185, 150, 83, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d4af37 0%, #b99653 100%)",
                  boxShadow: "0 6px 16px rgba(185, 150, 83, 0.4)",
                },
              }}
            >
              View All Submissions
            </Button>
          </Link>
        )}
      </StyledMenu>
    </div>
  );
}
